import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import ClearIcon from '@mui/icons-material/Clear';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Autocomplete, InputAdornment, Paper, TextField } from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';

import asyncService from '../../../datahub/asyncService';
import { validateSchema } from '../../../main-deprecated/components/Form/validations';
import selectUser from '../../../main-deprecated/store/selectors/appSelector';
import Search from '../../assets/icons/SearchPrimaryColor.svg';
import theme from '../../theme';
import { BLACK, PRIMARY_COLOR, PRIMARY_PURPLE } from '../../theme/colorConstants';
import { PRIMARY_FONT } from '../../theme/fontConstants';

export const SEARCHABLE_DROPDOWN_VARIANT = {
  FILTER: 'filter',
  FORM: 'form',
  SEARCH: 'search',
};

const formVariantStyle = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    backgroundColor: BLACK[0],
    borderRadius: '6px',
    color: BLACK[100],
    minHeight: '40px',
    padding: '2px 39px 2px 12px !important',
  },
  '& .MuiInputBase-input': {
    fontFamily: PRIMARY_FONT[400],
    fontSize: '14px',
  },
  '& .MuiInputBase-input::placeholder': {
    color: BLACK[60],
    fontFamily: PRIMARY_FONT[400],
    opacity: 1,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: `1px solid ${BLACK[40]}`,
    borderRadius: '6px',
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: BLACK[50],
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    border: `1px solid ${PRIMARY_PURPLE[70]}`,
  },
  '& .MuiAutocomplete-popupIndicator': {
    color: BLACK[70],
  },
};

const getOptionsApiConfigSignature = (optionsAPICallback) => {
  if (!optionsAPICallback) return '';

  const { httpMethod = '', route = '', generateBody } = optionsAPICallback;
  let generatedBodySignature = '';

  if (typeof generateBody === 'function') {
    try {
      generatedBodySignature = JSON.stringify(generateBody('')) || '';
    } catch (_error) {
      generatedBodySignature = '';
    }
  }

  return `${httpMethod}::${route}::${generatedBodySignature}`;
};

/**
 * SearchableDropdown - A controlled component for searchable dropdowns with API-fetched options.
 *
 * This is a controlled component following React best practices:
 * - The `value` prop controls the selected option (full option object or null)
 * - The `onChange` callback receives the full option object (or null when cleared)
 * - Options are fetched from API based on search term
 * - No internal state for the selected value - parent component is the single source of truth
 *
 * @param {Object} props
 * @param {Object|null} props.value - The currently selected option object (or null)
 * @param {Function} props.onChange - Callback fired when selection changes: (event, newValue) => void
 * @param {Object} props.optionsAPICallback - Configuration for API call to fetch options
 * @param {Function} props.getOptionLabel - Function to get display label from option: (option) => string
 * @param {boolean} props.disabled - Whether the dropdown is disabled
 * @param {string} props.placeholder - Placeholder text
 * @param {Object} props.sx - MUI sx prop for TextField styling
 * @param {Object} props.wrapperSx - MUI sx prop for FormControl wrapper styling
 * @param {boolean} props.disableClear - Whether to disable clearing the selection
 * @param {string} props.variant - FILTER (compact pill), FORM (single select field) or
 *   SEARCH (an "add" input: value stays null and the text clears after each pick)
 * @param {string[]} props.excludeIds - Option _ids to hide from the list (e.g. already picked)
 */
const SearchableDropdown = ({
  value,
  onChange,
  optionsAPICallback,
  getOptionLabel,
  disabled = false,
  placeholder = '',
  sx,
  wrapperSx,
  disableClear = false,
  variant = SEARCHABLE_DROPDOWN_VARIANT.FILTER,
  testId,
  excludeIds = [],
}) => {
  const isFormVariant = variant === SEARCHABLE_DROPDOWN_VARIANT.FORM;
  const isSearchVariant = variant === SEARCHABLE_DROPDOWN_VARIANT.SEARCH;
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInputValue, setSearchInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const user = useSelector(selectUser);
  const dispatch = useDispatch();
  const token = user?.token;
  const refreshToken = user?.refreshToken;

  const debounceSearchDelay = 750;
  const searchTimeoutRef = useRef(null);
  const previousApiConfigSignatureRef = useRef(getOptionsApiConfigSignature(optionsAPICallback));

  const styleForFilterControl = {
    '& .MuiOutlinedInput-root': {
      paddingRight: '10px !important',
    },
    '& .MuiInputBase-inputAdornedEnd': {
      background: value && theme?.button?.palette?.primary,
      color: value && 'white',
      height: '6px',
      borderRadius: '6px',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: '0px',
    },
    '& .MuiInputBase-input::placeholder': {
      color: theme?.button?.palette?.primary,
      fontFamily: PRIMARY_FONT['400'],
      opacity: 1,
    },
    border: `1px solid ${theme?.searchBoxForFilter?.borderColor?.focus}`,
    borderRadius: '5px',
    justifyContent: 'center',
    height: '28px',
    width: '200px',
  };

  const styleForFormControl =
    isFormVariant || isSearchVariant ? formVariantStyle : styleForFilterControl;

  const loadOptions = useCallback(
    (term) => {
      if (disabled) return;
      const { contract, httpMethod, generateBody, route } = optionsAPICallback;

      const requestBody = generateBody(term);
      if (contract && validateSchema(requestBody, contract)?.FORM_ERROR) {
        return;
      }

      setLoading(true);

      asyncService({
        httpMethod,
        route,
        data: requestBody,
        onSuccess: (d) => {
          const apiResultDocuments = d?.data?.documents || [];
          setOptions(apiResultDocuments);
          setLoading(false);
        },
        onError: () => {
          setOptions([]);
          setLoading(false);
        },
        dispatch,
        token,
        refreshToken,
      });
    },
    [dispatch, optionsAPICallback, disabled, refreshToken, token]
  );

  // Clear cached options/search only when API config meaningfully changes.
  // This avoids destructive resets caused by non-memoized prop object identity changes.
  useEffect(() => {
    const nextApiConfigSignature = getOptionsApiConfigSignature(optionsAPICallback);

    if (previousApiConfigSignatureRef.current === nextApiConfigSignature) {
      return;
    }

    previousApiConfigSignatureRef.current = nextApiConfigSignature;
    setOpen(false);
    setLoading(false);
    setSearchTerm('');
    setOptions([]);
  }, [optionsAPICallback]);

  // Ensure the current value is in the options list when dropdown opens
  useEffect(() => {
    if (open && value && !disabled) {
      setOptions((prevOptions) => {
        // Check if current value is already in options
        const valueInOptions = prevOptions.some((option) => option?._id === value?._id);

        // If not, add it to options so it displays correctly
        if (!valueInOptions) {
          return [value, ...prevOptions];
        }
        return prevOptions;
      });
    }
  }, [open, value, disabled]);

  // Cleanup timeout on unmount to prevent memory leaks
  useEffect(
    () => () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    },
    []
  );

  const handleSearchInputChange = useCallback(
    (e) => {
      const { value: inputValue } = e.target;

      // Clear any existing timeout before setting a new one
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      searchTimeoutRef.current = setTimeout(() => {
        setSearchTerm(inputValue);
        loadOptions(inputValue);
      }, debounceSearchDelay);
    },
    [loadOptions]
  );

  const handleOnOptionSelect = useCallback(
    (event, newValue) => {
      // Prevent clearing if disableClear is true
      if (disableClear && newValue === null) {
        return;
      }

      if (isSearchVariant && newValue === null) {
        return;
      }

      if (newValue === null) {
        setOpen(false);
      }

      // Call parent's onChange with the full option object (or null)
      onChange(event, newValue);
    },
    [onChange, disableClear, isSearchVariant]
  );

  const handleInputChange = useCallback((event, nextInputValue, reason) => {
    setSearchInputValue(reason === 'reset' ? '' : nextInputValue);
  }, []);

  const handleClear = useCallback(
    (event) => {
      if (disableClear) return;

      event?.preventDefault?.();
      event?.stopPropagation?.();

      setSearchTerm('');
      setOptions([]);
      // Call onChange with null to clear the selection (matching MUI Autocomplete signature)
      onChange(event, null);
    },
    [onChange, disableClear]
  );

  const handleOpen = useCallback(() => {
    if (disabled) return;
    setOpen(true);
    // Load options when opening if we don't have any yet
    if (options.length === 0) {
      loadOptions(searchTerm);
    }
  }, [disabled, loadOptions, options.length, searchTerm]);

  const handleClose = useCallback(() => {
    setOpen(false);
    setLoading(false);
    // Clear search term and options on close
    setSearchTerm('');
    setOptions([]);
  }, []);

  const visibleOptions = excludeIds.length
    ? options.filter((option) => !excludeIds.includes(option?._id))
    : options;

  const searchVariantInputProps = isSearchVariant
    ? { inputValue: searchInputValue, onInputChange: handleInputChange }
    : {};

  return (
    <FormControl sx={{ ...styleForFormControl, ...wrapperSx }}>
      <Autocomplete
        value={isSearchVariant ? null : value || null}
        onChange={handleOnOptionSelect}
        {...searchVariantInputProps}
        disabled={disabled}
        popupIcon={isFormVariant ? <KeyboardArrowDownIcon data-testid="dropdown-chevron" /> : null}
        clearIcon={null}
        noOptionsText="No results found. Try searching for something else."
        loadingText={
          <Grid container justifyContent="space-between">
            <div> Loading results...</div>
            <CircularProgress size={22} />
          </Grid>
        }
        PaperComponent={(props) => (
          <Paper
            {...props}
            style={{
              borderRadius: '16px',
            }}
            placement="auto-start"
          />
        )}
        renderOption={(props, option, { selected }) => (
          <li
            {...props}
            style={{
              display: 'flex',
              backgroundColor: selected ? PRIMARY_COLOR['30'] : 'white',
              cursor: 'pointer',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = PRIMARY_COLOR['60'];
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = selected ? PRIMARY_COLOR['30'] : 'white';
            }}
          >
            {getOptionLabel(option)}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            {...{ inputProps: { ...params.inputProps, 'data-testid': testId } }}
            sx={sx}
            placeholder={placeholder}
            InputProps={{
              ...params.InputProps,
              startAdornment: isFormVariant ? null : (
                <InputAdornment position="start">
                  <Box
                    component="img"
                    sx={{
                      height: 16,
                      width: 16,
                    }}
                    alt="Search"
                    src={Search}
                  />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {value && !disableClear ? (
                    <ClearIcon
                      onClick={handleClear}
                      style={{ cursor: 'pointer', width: '12px', marginLeft: '8px' }}
                    />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            onChange={handleSearchInputChange}
            onBlur={() => {
              // Don't clear on blur - let user see their selection
            }}
          />
        )}
        isOptionEqualToValue={(option, val) => option?._id === val?._id}
        open={open && !disabled}
        onOpen={handleOpen}
        onClose={handleClose}
        loading={loading}
        options={visibleOptions || []}
        getOptionLabel={(option) => getOptionLabel(option) || ''}
      />
    </FormControl>
  );
};

SearchableDropdown.propTypes = {
  value: PropTypes.shape({
    _id: PropTypes.string,
  }),
  onChange: PropTypes.func.isRequired,
  optionsAPICallback: PropTypes.shape({
    httpMethod: PropTypes.string.isRequired,
    route: PropTypes.string.isRequired,
    generateBody: PropTypes.func.isRequired,
    contract: PropTypes.shape({}),
  }).isRequired,
  getOptionLabel: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  sx: PropTypes.shape({}),
  wrapperSx: PropTypes.shape({}),
  disableClear: PropTypes.bool,
  variant: PropTypes.oneOf(Object.values(SEARCHABLE_DROPDOWN_VARIANT)),
  testId: PropTypes.string,
  excludeIds: PropTypes.arrayOf(PropTypes.string),
};

export default SearchableDropdown;
